import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useAppDispatch } from 'app/hooks';
import {
  userSettingsApi,
  uploadUserSettingsSubmittalPdf,
  useCreateUserSettingsSubmittalFormatMutation,
  useCreateUserSettingsSubmittalUdfMutation,
  useDeleteUserSettingsSubmittalFormatMutation,
  useDeleteUserSettingsSubmittalUdfMutation,
  useDragUserSettingsSubmittalFormatMutation,
  useGetUserSettingsCmsTermsQuery,
  useGetUserSettingsSubmittalFormatQuery,
  useGetUserSettingsSubmittalPredefinedFieldsQuery,
  useGetUserSettingsSubmittalUserDefinedFieldsQuery,
  useGetUserSettingsUdfTypeOptionsQuery,
  usePatchUserSettingsSubmittalFormatMutation,
  usePatchUserSettingsSubmittalUdfMutation,
  type IUserSettingsPredefinedField,
  type IUserSettingsSubmittalFormatRow,
  type IUserSettingsSubmittalOrderEntry,
  type IUserSettingsSubmittalUdf,
  type IUserSettingsSubmittalUdfPatch,
} from 'app/services/userSettingsApi';
import { useLibraryDrag } from '../../useLibraryDrag';
import FieldDetail from './FieldDetail';
import FieldLibrary, { libraryItemKey, type LibraryItemKey } from './FieldLibrary';
import FormatRail, { type FormatRailVariant } from './FormatRail';
import UserFieldPane from './UserFieldPane';
import { DeleteFormatFieldPopup, DeleteUdfPopup, UdfTypePopup } from './SubmittalPopups';
import {
  deriveSubmittalCmsDocuments,
  jqHeight,
  SUBMITTAL_DRAG_MODELS,
  SUBMITTAL_FIELD_MODELS,
  type SubmittalDragPayload,
  type SubmittalLibraryType,
} from './submittalModel';

/**
 * The Submittal tab — `submittal/view/submittal.js` (951 LOC) +
 * `submittal/tmpl/submittal.tmpl` (320). Waves U-b (read-only), U-c (the
 * User-Defined-Field lifecycle, including the PDF cut-sheet upload) and U-d
 * (the format rail's three writes: reorder, drop-to-create, delete).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ★★ THE ARCHITECTURE, AND WHY IT LOOKS LIKE THIS ★★
 *
 * A Backbone view has two layers that React usually collapses into one, and on
 * THIS view the gap between them is load-bearing:
 *
 *   · the COLLECTIONS (`self.collectionUserDefinedFields`,
 *     `self.collectionSubmittalFormat`) — mutated freely by every handler;
 *   · the DOM, which is only rebuilt when `render()` is explicitly called.
 *
 * `render()` is called exactly THREE times in this view's reachable life: once
 * when the five boot fetches resolve (`:147`), once after a UDF is created
 * (`:267`), and once at the tail of a drop-to-create — `fetch_submittal_format`
 * (`:692-702`) reached through `Dispatcher 'submittal:format:changed'`. Every
 * other write — rename, content edit, checkbox, PDF upload, UDF delete, format
 * reorder, format-row delete — mutates the collection and then patches
 * individual DOM nodes by hand, deliberately leaving the rendered markup stale.
 * That staleness IS the behaviour (see §6 W2's "No re-fetch, no re-render" and
 * the search-vs-rename note in `FieldLibrary.tsx`).
 *
 * So this component keeps the same two layers:
 *   · `fieldsRef` / `formatRef`  = the collections. Never read during render.
 *   · `snap` / `rail` / `counts` = what the template was last handed.
 *   · `doRender()`               = `render()`. The ONLY copy from refs to
 *                                  snapshot, called from the same two places.
 *   · `labelOverrides` / `railLabelOverrides` / `filledIds` / `pdfViews` =
 *                                  the imperative post-render DOM patches,
 *                                  all cleared by `doRender` because
 *                                  `$el.html()` throws them away too.
 *
 * A naive "just derive everything from the query cache" port would silently fix
 * five quirks at once (Q1, Q6, the stale search index, the vanishing PDF, the
 * blank-panes-after-create reset). Do not collapse the layers.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * SEAMS — what is deliberately NOT here:
 *   U-e  the whole Proposal tab (`#proposal_format`). It is this file's
 *        structural twin against `/api/v1.0/user_settings/prp/…`, minus the PDF
 *        upload and minus the type popup, plus Q12/Q13. Everything U-d added
 *        below is written to be transcribed rather than shared: the format-rail
 *        handlers read `formatRef` / `rail` and nothing global, and the drag
 *        vocabulary already lives in `submittalModel.ts`. The proposal sortable
 *        uses `items: '.item-for-drag'` and its own `/prp/format/drag/`.
 *   U-f  the ↑/↓ format-row navigation and the Esc/Enter popup keys — both live
 *        on the page shell (`view/main.js`), not here.  ✓ LANDED, as
 *        `useFilterNavigation.ts` and `usePopupKeys({blanket:true})`, both
 *        mounted by `UserSettingsPage`. The walk skips `.empty-row` (the
 *        sentinel `FormatRail` renders for the `render` variant only) and drives
 *        THIS file's `onRowClick` / `onItemClick` / `onRowMouseOver` through a
 *        click it SYNTHESISES 300 ms after the keypress — which is why
 *        `FormatRail` now tests `e.detail !== 0` before Q22's plain-click drag
 *        PATCH: legacy's arrow walk is zero-network, and without that test every
 *        keypress would emit a reorder.
 * ═════════════════════════════════════════════════════════════════════════════
 */

interface SubmittalTabProps {
  /** Whether `#submittal_format` is the visible pane. Only `showTooltip` cares:
   * `Dispatcher 'open:submittal'` re-runs it (`:128`), and overflow cannot be
   * measured while the pane is `display:none`. */
  active: boolean;
}

/** The data `render()` hands the template. */
interface SubmittalSnapshot {
  gen: number;
  userFields: IUserSettingsSubmittalUdf[];
}

const EMPTY_APP_FIELDS: IUserSettingsPredefinedField[] = [];

/**
 * The synthetic `id` carried by `createNewField`'s hand-injected rail row.
 * Negative so it can never collide with a real format-row id, and never
 * rendered — the row's DOM id and trash `data-id` come from `domIdOverrides`,
 * which maps this key onto the LIBRARY item's id (`''` for CMS). See the
 * `domIdOverrides` prop on `FormatRail`.
 */
const INJECTED_FORMAT_ROW_ID = -1;

const SubmittalTab = ({ active }: SubmittalTabProps) => {
  const dispatch = useAppDispatch();

  // ── the five boot fetches (`submittal.js:130-136`, one `Promise.all`) ─────
  const { data: appFieldsData } = useGetUserSettingsSubmittalPredefinedFieldsQuery();
  const { data: udfData } = useGetUserSettingsSubmittalUserDefinedFieldsQuery();
  const { data: formatData } = useGetUserSettingsSubmittalFormatQuery();
  const { data: typeOptions } = useGetUserSettingsUdfTypeOptionsQuery();
  const { data: terms } = useGetUserSettingsCmsTermsQuery();

  const appFields = appFieldsData ?? EMPTY_APP_FIELDS;
  const cmsDocuments = useMemo(() => deriveSubmittalCmsDocuments(terms), [terms]);

  const [createUdf] = useCreateUserSettingsSubmittalUdfMutation();
  const [patchUdf] = usePatchUserSettingsSubmittalUdfMutation();
  const [deleteUdf] = useDeleteUserSettingsSubmittalUdfMutation();
  // U-d — §6 W7 / W8 / W9.
  const [createFormat] = useCreateUserSettingsSubmittalFormatMutation();
  const [dragFormat] = useDragUserSettingsSubmittalFormatMutation();
  const [patchFormat] = usePatchUserSettingsSubmittalFormatMutation();
  const [deleteFormat] = useDeleteUserSettingsSubmittalFormatMutation();

  // ── layer 1: the collections ─────────────────────────────────────────────
  const fieldsRef = useRef<IUserSettingsSubmittalUdf[]>([]);
  const formatRef = useRef<IUserSettingsSubmittalFormatRow[]>([]);

  // ── layer 2: what the template was last handed ───────────────────────────
  const [snap, setSnap] = useState<SubmittalSnapshot | null>(null);
  const [rail, setRail] = useState<{
    rows: IUserSettingsSubmittalFormatRow[];
    variant: FormatRailVariant;
  }>({ rows: [], variant: 'render' });
  const [counts, setCounts] = useState({ user: 0, cms: 0, application: 0 });

  // ── view state that SURVIVES a render (`self.*` on the Backbone view) ────
  const [activeType, setActiveType] = useState<SubmittalLibraryType | undefined>('user');
  const activeTypeRef = useRef<SubmittalLibraryType | undefined>('user');
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const activeRowIdRef = useRef<string | null>(null);

  // ── view state that a render RESETS (the template rebuilds it) ───────────
  const [searchValue, setSearchValue] = useState('');
  const [searchHidden, setSearchHidden] = useState(false);
  const [activeItemKey, setActiveItemKey] = useState<string | null>(null);
  const activeItemKeyRef = useRef<string | null>(null);
  /**
   * ★ `active_last_change` (`:478-484`) adds `.active` to a library `<li>` with
   * `addClass` — it does NOT remove it from the row that already has it. So
   * legacy can legitimately show TWO grey `.nav-item.active.user-tab` bands at
   * once (`styles.css:2446`), and it does exactly that whenever the edited
   * field's `id` collides with some other field's `field_order` (verified live:
   * saving field 309's content highlights BOTH 309 and 392, because 392's
   * `field_order` is 309).
   *
   * `activeItemKey` above is the EXCLUSIVE one — the row Bootstrap's tab plugin
   * selected — and cannot express that. This set is the additive half. It is
   * written only by `activeLastChange`, and cleared everywhere legacy's DOM
   * would lose the class: a full `render()`, `changeActiveDefinedsField`'s
   * `$('#submittal .each-element-row.active').removeClass('active in')`, and any
   * library click (Bootstrap's `activate()` strips `.active` from the `<ul>`'s
   * previous child before adding it to the new one).
   */
  const [extraActiveKeys, setExtraActiveKeys] = useState<ReadonlySet<string>>(() => new Set());
  const extraActiveKeysRef = useRef<ReadonlySet<string>>(new Set());
  const [activePaneId, setActivePaneId] = useState<string | null>(null);
  const activePaneIdRef = useRef<string | null>(null);
  const [panesWithIn, setPanesWithIn] = useState<ReadonlySet<string>>(() => new Set());
  const [filledIds, setFilledIds] = useState<ReadonlySet<number>>(() => new Set());
  const [labelOverrides, setLabelOverrides] = useState<Record<number, string>>({});
  const [railLabelOverrides, setRailLabelOverrides] = useState<Record<string, string>>({});
  const [pdfViews, setPdfViews] = useState<Record<number, { src: string; error: string | null }>>({});
  const [tooltipItemKeys, setTooltipItemKeys] = useState<ReadonlySet<string>>(() => new Set());
  const [tooltipRowIds, setTooltipRowIds] = useState<ReadonlySet<number>>(() => new Set());
  const [invalidTitles, setInvalidTitles] = useState<ReadonlySet<string>>(() => new Set());

  /**
   * ★ U-d — the DOM-id override for `createNewField`'s hand-injected rail row.
   * See `FormatRail`'s `domIdOverrides` prop: the injected row renders its
   * `row-…` id and its trash `data-id` from the LIBRARY item's id, because
   * `templateSelectItem` is handed `{id: target_id, …}` (`:808-815`). Reset by
   * `doRender`, exactly like every other post-render patch above.
   */
  const [domIdOverrides, setDomIdOverrides] = useState<Record<number, string>>({});

  // ── popups ───────────────────────────────────────────────────────────────
  const [typePopupOpen, setTypePopupOpen] = useState(false);
  const [deletePopupField, setDeletePopupField] = useState<IUserSettingsSubmittalUdf | null>(null);
  /**
   * ★★ Q4 — AN ARRAY, NOT A NULLABLE SINGLE POPUP. One click on a rail trash
   * button opens TWO identical `.delete-field` popups (the `<i>` and the
   * `<button>` both match `'click #submittal .delete'` and the handler never
   * stops the bubble). Confirming or cancelling clears the whole array, because
   * `remove_confirm_popup` removes `#popup-content.delete-field` as a SET — and
   * exactly one DELETE goes out. See `DeleteFormatFieldPopup`.
   */
  const [deleteFormatPopups, setDeleteFormatPopups] = useState<IUserSettingsSubmittalFormatRow[]>(
    []
  );

  // ── the remaining `self.*` scalars, verbatim ─────────────────────────────
  /** `self.data` — the accumulating PATCH body. ★ SHARED ACROSS FIELDS, which
   * is exactly how Q5 leaks a checkbox value onto a different field's save. */
  const dataRef = useRef<IUserSettingsSubmittalUdfPatch>({});
  /** `self.field_id`, initialised to `1` at `submittal.js:112`. */
  const fieldIdRef = useRef<number>(1);
  /** `self.selected_field` — set by `render_popup`, read as the save fallback. */
  const selectedFieldRef = useRef<number | null>(null);
  /** `self.type_format` — the NUMBER 2 until a radio `change` makes it a string. */
  const typeFormatRef = useRef<number | string>(2);
  /** `self.name_change`. Set true by every save; only ever read by the
   * collection `change` listener that suppresses a re-render (`:119-123`).
   * Kept so the suppression is explicit rather than accidental. */
  const nameChangeRef = useRef(false);
  /** `self.active_row_clicked` — makes the NEXT `clickToItem` scroll. */
  const activeRowClickedRef = useRef(false);
  /** `self.last_clicked` — the `<li>`'s `data-id`, re-clicked by `render_selects`. */
  const lastClickedRef = useRef<string | null>(null);
  /** `self.selected_format` — the active row's `data-target`. */
  const selectedFormatRef = useRef<number | null>(null);
  /** `self.selected_field_id` — the format row's `data-id`, as the STRING it
   * came off the attribute as, set by `render_delete_popup_submittal` (`:640`)
   * and read by `removeSubmittalFormatField` (`:707,709`). */
  const selectedFieldIdRef = useRef<string | null>(null);
  /** `self.last_action` — set to `'create_new_field'` by `createNewField`
   * (`:797`) purely to suppress the re-fetch in `fetch_submittal_format`. */
  const lastActionRef = useRef<string>('');
  /** Legacy's `self.dropping` (`:872,876`), with the dropped item's four
   * attributes attached. Non-null exactly while a LIBRARY drag is in flight,
   * which is the single question the sortable's `stop` branches on. */
  const dragPayloadRef = useRef<SubmittalDragPayload | null>(null);

  const railWrapperRef = useRef<HTMLDivElement | null>(null);
  /** `#submittal-format-list-wrapper-row` — `connectToSortable`'s target, and
   * therefore the only box a library drag may be released over. */
  const railRef = useRef<HTMLDivElement | null>(null);
  const labelRefs = useRef(new Map<number, HTMLSpanElement>());
  const itemRefs = useRef(new Map<string, HTMLLIElement>());
  const anchorRefs = useRef(new Map<string, HTMLAnchorElement>());
  const renderSelectsTimer = useRef<number | undefined>(undefined);

  const setActiveRow = useCallback((value: string | null) => {
    activeRowIdRef.current = value;
    setActiveRowId(value);
  }, []);
  const setActiveItem = useCallback((value: string | null) => {
    activeItemKeyRef.current = value;
    setActiveItemKey(value);
  }, []);
  const setExtraActive = useCallback((value: ReadonlySet<string>) => {
    extraActiveKeysRef.current = value;
    setExtraActiveKeys(value);
  }, []);
  const setActivePane = useCallback((value: string | null) => {
    activePaneIdRef.current = value;
    setActivePaneId(value);
  }, []);

  /**
   * `render()` (`submittal.js:163-194`). Rebuilds the whole pane from the
   * collections and therefore ANNIHILATES every imperative patch: the filled
   * detail panes go back to empty (★ Q1 all over again), the search box empties
   * and un-hides, the renamed labels revert to the collection's values, the
   * active library row and pane are cleared, and any uploaded-but-not-persisted
   * PDF src is lost. `self.active_row_id` is the one thing restored (`:180-181`)
   * — and only if that row still exists.
   */
  const doRender = useCallback(
    (fields: IUserSettingsSubmittalUdf[], format: IUserSettingsSubmittalFormatRow[]) => {
      fieldsRef.current = fields;
      formatRef.current = format;
      setSnap((prev) => ({ gen: (prev?.gen ?? 0) + 1, userFields: fields }));
      setRail({ rows: format, variant: 'render' });
      setCounts({ user: fields.length, application: appFields.length, cms: cmsDocuments.length });
      setFilledIds(new Set());
      setPanesWithIn(new Set());
      setLabelOverrides({});
      setRailLabelOverrides({});
      // U-d: the hand-injected rail row and its borrowed DOM id are template
      // output like everything else — `$el.html()` throws them away too.
      setDomIdOverrides({});
      setPdfViews({});
      setActiveItem(null);
      setActivePane(null);
      setSearchValue('');
      setSearchHidden(false);
      setTooltipItemKeys(new Set());
      setTooltipRowIds(new Set());
      setExtraActive(new Set());
      const keep =
        activeRowIdRef.current && format.some((r) => `row-${r.id}` === activeRowIdRef.current)
          ? activeRowIdRef.current
          : null;
      setActiveRow(keep);
    },
    [appFields.length, cmsDocuments.length, setActiveItem, setActivePane, setActiveRow, setExtraActive]
  );

  /** RTK Query cache mirror — see the "STILL NO TAG TYPES" banner in
   * `userSettingsApi.ts`. Zero network; it only keeps the cache from
   * resurrecting a deleted row if the SPA remounts this route. */
  const patchUdfCache = useCallback(
    (recipe: (draft: IUserSettingsSubmittalUdf[]) => void) => {
      dispatch(
        userSettingsApi.util.updateQueryData(
          'getUserSettingsSubmittalUserDefinedFields',
          undefined,
          recipe
        )
      );
    },
    [dispatch]
  );

  // ═══════════════════════════════════════════════════════════════════════
  // BOOT — `initialize`'s `Promise.all(...).then(...)` (`:130-149`)
  // ═══════════════════════════════════════════════════════════════════════
  const booted = useRef(false);
  useEffect(() => {
    if (booted.current) return;
    if (!appFieldsData || !udfData || !formatData || !typeOptions || !terms) return;
    booted.current = true;
    doRender(udfData, formatData);
    /**
     * ★★ Q1's FIRST HALF — `:148`:
     *   `…li.each-element-row:nth-child(1) a.submittal-item-for-drag').click()`
     * A CLICK. It runs Bootstrap's tab plugin (so the first pane becomes
     * `.active in` and is the visible one) and `clickToItem`, but a click does
     * NOT fire `mouseover`, and `loadPdf` is bound to `mouseover`. So the pane
     * that is showing is the one pane guaranteed to be EMPTY. `filledIds` is
     * deliberately left untouched here.
     */
    const first = udfData[0];
    if (!first) return;
    const paneId = `submittal_user_defined_field_${first.id}`;
    setActiveItem(libraryItemKey('user', first.id));
    setActivePane(paneId);
    setPanesWithIn(new Set([paneId]));
    lastClickedRef.current = String(first.id);
    selectedFormatRef.current = null;
  }, [
    appFieldsData,
    udfData,
    formatData,
    typeOptions,
    terms,
    doRender,
    setActiveItem,
    setActivePane,
  ]);

  // ═══════════════════════════════════════════════════════════════════════
  // showTooltip (`:882-890`) — measured after every render and on tab open
  // ═══════════════════════════════════════════════════════════════════════
  useLayoutEffect(() => {
    if (!snap) return;
    // ★ Only `.user-defiened-fields` (the USER anchors) and the format rail's
    // `.submittal_format_field` spans are measured; CMS and Application rows
    // never get a tooltip.
    const nextItems = new Set<string>();
    anchorRefs.current.forEach((el, key) => {
      if (key.startsWith('user:') && el.scrollWidth > el.offsetWidth) nextItems.add(key);
    });
    const nextRows = new Set<number>();
    labelRefs.current.forEach((el, id) => {
      if (el.scrollWidth > el.offsetWidth) nextRows.add(id);
    });
    // Legacy only ever ADDS the class (`classList.add`), and nothing removes it
    // short of a full template rebuild — so union, and let `doRender` reset.
    setTooltipItemKeys((prev) => {
      const merged = new Set(prev);
      nextItems.forEach((k) => merged.add(k));
      return merged.size === prev.size ? prev : merged;
    });
    setTooltipRowIds((prev) => {
      const merged = new Set(prev);
      nextRows.forEach((k) => merged.add(k));
      return merged.size === prev.size ? prev : merged;
    });
  }, [snap, rail, active]);

  useEffect(
    () => () => {
      if (renderSelectsTimer.current !== undefined) window.clearTimeout(renderSelectsTimer.current);
    },
    []
  );

  // ═══════════════════════════════════════════════════════════════════════
  // READ-SIDE HANDLERS (U-b)
  // ═══════════════════════════════════════════════════════════════════════

  /** `loadPdf` (`:599-618`) — fills ONE user pane, once. The iframe's `src` is
   * set from `data-placeholder` at the same moment, which is what `pdfViews`'
   * default value below expresses. */
  const loadPdfForField = useCallback((fieldId: number) => {
    setFilledIds((prev) => (prev.has(fieldId) ? prev : new Set(prev).add(fieldId)));
  }, []);

  /**
   * `clickToItem` (`:916-934`). Runs on EVERY library-row click, including the
   * ones `rowClick`, `removeUserDefinedField` and `render_selects` synthesise.
   *
   * ★ The clicked row's own type is deliberately NOT a factor: legacy compares
   * the ACTIVE format row's type against `self.activeDefinedsField`, not
   * against the clicked element. `_type` is kept in the signature only so the
   * call sites read like the legacy handlers they came from.
   */
  const clickToItem = useCallback(
    (_type: SubmittalLibraryType, item: LibraryItemKey) => {
      lastClickedRef.current = item.liId;
      selectedFormatRef.current = null;
      const activeRow = formatRef.current.find((r) => `row-${r.id}` === activeRowIdRef.current);
      if (activeRow) {
        const rowType = SUBMITTAL_FIELD_MODELS[activeRow.model ?? ''];
        const target = activeRow.object_id == null ? '' : String(activeRow.object_id);
        const cms = activeRow.cms_field_name ?? '';
        const clickedId = item.anchorId == null ? '' : String(item.anchorId);
        if (rowType !== activeTypeRef.current || (target !== clickedId && !(cms && cms === item.cms))) {
          setActiveRow(null);
        }
      }
      if (activeRowClickedRef.current) {
        itemRefs.current.get(item.key)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      activeRowClickedRef.current = false;
    },
    [setActiveRow]
  );

  /**
   * One library row click = Bootstrap's `tab('show')` (exclusive `.active` on
   * the `<li>`, exclusive `.active`+sticky `.in` on the pane) PLUS the three
   * `app_tab`/`user_tab`/`cms_tab` handlers, which strip `.active` from the
   * OTHER two `<ul>`s so the three lists behave as one exclusive group, PLUS
   * `clickToItem`. All three are indistinguishable from outside, so they are
   * one function here.
   */
  const selectLibraryItem = useCallback(
    (type: SubmittalLibraryType, item: LibraryItemKey) => {
      setActiveItem(item.key);
      // Bootstrap's `activate()` strips `.active` off the `<ul>`'s previous
      // child before adding it to the clicked one — so an additive highlight
      // left behind by `active_last_change` does not survive the next click.
      if (extraActiveKeysRef.current.size) setExtraActive(new Set());
      setActivePane(item.paneId);
      setPanesWithIn((prev) => (prev.has(item.paneId) ? prev : new Set(prev).add(item.paneId)));
      clickToItem(type, item);
    },
    [clickToItem, setActiveItem, setActivePane, setExtraActive]
  );

  /** `changeActiveDefinedsField` (`:716-743`). */
  const changeActiveDefinedsField = useCallback(
    (type: SubmittalLibraryType | undefined) => {
      if (activeTypeRef.current === type) return; // legacy's early return (`:718`)
      activeTypeRef.current = type;
      setActiveType(type);
      // ★ The search box is `.hide`-ed for CMS/Application — but ONLY here.
      // `render()` re-emits it unhidden, which is why `doRender` resets this.
      setSearchHidden(type !== 'user');
      // `$('#submittal .each-element-row.active').removeClass('active in')` and
      // the same for `#myTabContent .active` — note BOTH classes go, so the
      // pane loses Bootstrap's sticky `.in` too.
      const wasPane = activePaneIdRef.current;
      setPanesWithIn((prev) => {
        if (!wasPane || !prev.has(wasPane)) return prev;
        const next = new Set(prev);
        next.delete(wasPane);
        return next;
      });
      setActiveItem(null);
      // `$('#submittal .each-element-row.active').removeClass('active in')` —
      // EVERY `.active` row goes, including an additive one.
      if (extraActiveKeysRef.current.size) setExtraActive(new Set());
      setActivePane(null);
    },
    [setActiveItem, setActivePane, setExtraActive]
  );

  /** Find the library row a format row points at — the target of `rowClick`'s
   * synthesised `.click()` (`:905-914`). */
  const libraryItemForRow = useCallback(
    (type: SubmittalLibraryType, row: IUserSettingsSubmittalFormatRow): LibraryItemKey | null => {
      if (row.cms_field_name) {
        // `a[data-cms="<cms>"]` — CMS anchors only.
        const doc = cmsDocuments.find((d) => d.value === row.cms_field_name);
        if (!doc) return null;
        return {
          key: libraryItemKey('cms', doc.value),
          liId: doc.value,
          cms: doc.value,
          paneId: `submittal_cms_${doc.key}`,
        };
      }
      // `a[data-id="<object_id>"]` inside the row's own `.submittal-row`.
      if (type === 'user') {
        const field = (snap?.userFields ?? []).find((f) => f.id === row.object_id);
        if (!field) return null;
        return {
          key: libraryItemKey('user', field.id),
          liId: String(field.id),
          anchorId: field.id,
          paneId: `submittal_user_defined_field_${field.id}`,
        };
      }
      if (type === 'application') {
        const field = appFields.find((f) => f.id === row.object_id);
        if (!field) return null;
        return {
          key: libraryItemKey('application', field.value),
          liId: `app-${field.value}`,
          anchorId: field.id,
          paneId: `submittal_${field.value}`,
        };
      }
      return null;
    },
    [appFields, cmsDocuments, snap]
  );

  /** `rowClick` (`:895-915`) — the cross-highlight from rail to library.
   * ★ `self.active_row_id = $(e.currentTarget).attr('id')` (`:899`) reads the id
   * back OUT of the DOM, so it is `row-<domId>` — which for the hand-injected
   * drag-created row is the library item's id, not the format row's (and the
   * bare `row-` for a CMS drop). Hence `domId` rather than `row.id`. */
  const onRowClick = useCallback(
    (row: IUserSettingsSubmittalFormatRow, domId: string) => {
      setActiveRow(`row-${domId}`);
      selectedFormatRef.current = row.object_id;
      activeRowClickedRef.current = true;
      const type = SUBMITTAL_FIELD_MODELS[row.model ?? ''];
      if (!type) return; // a placeholder row (`model: null`) highlights and stops
      // `$('.submittal-block-fields-wrap[data-type=…]').click()` — the chip
      // handler is bound to `.not-active`, so this is a no-op when the chip is
      // already the active one.
      changeActiveDefinedsField(type);
      const item = libraryItemForRow(type, row);
      if (item) selectLibraryItem(type, item);
    },
    [changeActiveDefinedsField, libraryItemForRow, selectLibraryItem, setActiveRow]
  );

  /** `startLoadPdf` (`:891-894`) — hovering a rail row that points at a UDF
   * pre-fills that field's detail pane (without activating it). */
  const onRowMouseOver = useCallback(
    (row: IUserSettingsSubmittalFormatRow) => {
      if (row.model !== 'submittaluserdefinedfield') return;
      const field = (snap?.userFields ?? []).find((f) => f.id === row.object_id);
      if (field) loadPdfForField(field.id);
    },
    [loadPdfForField, snap]
  );

  /** `check_scroll` (`:781-785`) — a hard-coded 40 px row height (Q24). */
  const onWrapperScroll = useCallback((wrapper: HTMLDivElement) => {
    wrapper.scrollLeft = 0;
    const max = (formatRef.current.length + 3) * 40 - jqHeight(wrapper);
    if (wrapper.scrollTop > max) wrapper.scrollTop = max;
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // WRITE-SIDE HANDLERS (U-c)
  // ═══════════════════════════════════════════════════════════════════════

  /** `render_selects` (`:196-219`) — a 300 ms-debounced re-render of the RAIL
   * ONLY (plus the User counter), from `submittal.format.list.tmpl`. Reached
   * from exactly one place: `removeUserDefinedField`. */
  const renderSelects = useCallback(() => {
    if (renderSelectsTimer.current !== undefined) window.clearTimeout(renderSelectsTimer.current);
    renderSelectsTimer.current = window.setTimeout(() => {
      setRail({ rows: [...formatRef.current], variant: 'renderSelects' });
      // `$('.submittal-fields-count.user .count').text(...)` — the ONLY place
      // the counter moves after a delete, which is why the `<li>` disappears
      // 300 ms before the number does.
      setCounts((prev) => ({ ...prev, user: fieldsRef.current.length }));
      // `$('.submittal-row a[data-id="<selected_format>"]').click()` then
      // `$el.find('li[data-id="<last_clicked>"] a').click()`.
      const selected = selectedFormatRef.current;
      if (selected != null) {
        const field = fieldsRef.current.find((f) => f.id === selected);
        if (field) {
          selectLibraryItem('user', {
            key: libraryItemKey('user', field.id),
            liId: String(field.id),
            anchorId: field.id,
            paneId: `submittal_user_defined_field_${field.id}`,
          });
        }
      }
      const last = lastClickedRef.current;
      if (last) {
        const field = fieldsRef.current.find((f) => String(f.id) === last);
        if (field) {
          selectLibraryItem('user', {
            key: libraryItemKey('user', field.id),
            liId: String(field.id),
            anchorId: field.id,
            paneId: `submittal_user_defined_field_${field.id}`,
          });
        }
      }
      renderSelectsTimer.current = undefined;
    }, 300);
  }, [selectLibraryItem]);

  /** `updateUserDefinedField` (`:304-316`) — accumulates into `self.data`. */
  const updateUserDefinedField = useCallback(
    (attr: 'name' | 'content', value: string, fieldId: number) => {
      fieldIdRef.current = fieldId;
      dataRef.current[attr] = value;
    },
    []
  );

  /** `active_last_change` (`:478-484`). ★ Ported with its own bug intact: it
   * looks the `<li>` up by `submittal_udf_item_field_<ID>` while the template
   * builds that id from `field_order`, so the `.active` it adds usually lands on
   * nothing, while the `loadPdf` on the next line uses the CORRECT `field_order`.
   *
   * ★ When it DOES land on something — the edited field's `id` happening to equal
   * another field's `field_order`, e.g. 309 ↔ 392 on dev3 — legacy `addClass`es
   * without removing, so BOTH rows end up grey. That is why the hit goes into the
   * additive `extraActiveKeys` and NOT into the exclusive `activeItemKey`:
   * assigning the exclusive one would move the highlight off the row the user is
   * actually editing, which legacy never does. */
  const activeLastChange = useCallback(
    (id: number, fieldOrder: number) => {
      const byWrongKey = fieldsRef.current.find((f) => f.field_order === id);
      if (byWrongKey) setExtraActive(new Set([libraryItemKey('user', byWrongKey.id)]));
      const paneId = `submittal_user_defined_field_${id}`;
      setActivePane(paneId);
      setPanesWithIn((prev) => (prev.has(paneId) ? prev : new Set(prev).add(paneId)));
      const byOrder = fieldsRef.current.find((f) => f.field_order === fieldOrder);
      if (byOrder) loadPdfForField(byOrder.id);
    },
    [loadPdfForField, setActivePane, setExtraActive]
  );

  /**
   * `saveUserDefinedFieldData` (`:446-472`) — §6 W2 / W3 / W4.
   *
   * ★★ Q5 IS THIS FUNCTION'S DIRTY-CHECK, NOT THE CHECKBOX HANDLER.
   * `self.data` is a single object shared by every field on the tab. The
   * checkbox writes into it and never flushes; the next title/content blur
   * flushes WHATEVER is in it, under `self.field_id` — which the checkbox never
   * updates. So the checkbox value can (a) never arrive, or (b) arrive attached
   * to a different field's PATCH. Both are reproduced by keeping one
   * `dataRef` / `fieldIdRef` pair for the whole tab, exactly as legacy does.
   */
  const saveUserDefinedFieldData = useCallback(
    (targetIsName: boolean, targetValue: string) => {
      nameChangeRef.current = true;
      const name = dataRef.current.name;
      const id = fieldIdRef.current || selectedFieldRef.current;
      const model = fieldsRef.current.find((f) => f.id === Number(id));
      if (!model) return;
      const body = { ...dataRef.current };
      let hasChanges = false;
      (Object.keys(body) as (keyof IUserSettingsSubmittalUdfPatch)[]).forEach((key) => {
        // Legacy compares with `!=`; kept loose so the coercions match.
        // eslint-disable-next-line eqeqeq
        if (body[key] != model[key]) hasChanges = true;
      });
      if (!hasChanges) return; // ★ `self.data` is NOT cleared on this branch
      patchUdf({ id: model.id, body });
      dataRef.current = {};
      // Backbone's `save(attrs, {patch:true})` `set`s first — the collection
      // learns the new values even though the DOM is not re-rendered.
      fieldsRef.current = fieldsRef.current.map((f) =>
        f.id === model.id ? { ...f, ...body } : f
      );
      patchUdfCache((draft) => {
        const row = draft.find((f) => f.id === model.id);
        if (row) Object.assign(row, body);
      });
      if (name) {
        // `:459-465` — the format rail's labels are patched IN PLACE (both the
        // rendered text and the in-memory collection), with no re-render.
        // ⚠ Legacy also does `$('.select-row[data-target=…]').data('name', name)`,
        // which writes jQuery's internal data store and NOT the DOM attribute —
        // invisible and unread by anything. Deliberately not reproduced.
        setRailLabelOverrides((prev) => ({ ...prev, [String(model.id)]: name }));
        formatRef.current = formatRef.current.map((r) =>
          r.object_id === model.id ? { ...r, name } : r
        );
      }
      if (targetIsName) {
        // `:466-469` — `a.innerText = e.target.value; return`. The EARLY RETURN
        // is why renaming never runs `active_last_change`.
        setLabelOverrides((prev) => ({ ...prev, [model.id]: targetValue }));
        return;
      }
      activeLastChange(model.id, model.field_order);
    },
    [activeLastChange, patchUdf, patchUdfCache]
  );

  const onTitleBlur = useCallback(
    (field: IUserSettingsSubmittalUdf, value: string) => {
      // `blur .user_defined_field` is bound BEFORE `blur .content_title`
      // (`submittal.js:59` vs `:63`), and jQuery fires delegated handlers in
      // binding order, so `updateUserDefinedField` always runs first.
      updateUserDefinedField('name', value, field.id);
      saveUserDefinedFieldData(true, value);
    },
    [saveUserDefinedFieldData, updateUserDefinedField]
  );

  const onContentBlur = useCallback(
    (field: IUserSettingsSubmittalUdf, html: string) => {
      updateUserDefinedField('content', html, field.id);
      saveUserDefinedFieldData(false, html);
    },
    [saveUserDefinedFieldData, updateUserDefinedField]
  );

  /** `changeNewPage` (`:440-445`) — ★ Q5. Writes `self.data` and issues NOTHING. */
  const onNewPageChange = useCallback((checked: boolean) => {
    dataRef.current.checkbox_start_from_new_page = checked;
  }, []);

  /** `validatePDF` (`:365-377`) — runs BEFORE any request. */
  const validatePdf = (file: File | undefined): string | null => {
    if (!file) return 'No file selected.';
    if (file.type !== 'application/pdf') return 'Incorrect file extension. Only PDF allowed.';
    if (file.size / 1048576 >= 30) return 'Maximum file size of 30MB exceeded.';
    return null;
  };

  /** `errorMSG` (`:379-392`) — removes the `<iframe>` and shows
   * `<span id="span_error">` in its place. */
  const showPdfError = useCallback((fieldId: number, message: string) => {
    setPdfViews((prev) => ({
      ...prev,
      [fieldId]: { src: prev[fieldId]?.src ?? '', error: message },
    }));
  }, []);

  /** `uploadPDF` (`:324-362`) — §6 W5, the multipart PATCH. */
  const onPdfFileChange = useCallback(
    async (field: IUserSettingsSubmittalUdf, input: HTMLInputElement) => {
      const file = input.files?.[0];
      const error = validatePdf(file ?? undefined);
      if (error) {
        showPdfError(field.id, error);
        return;
      }
      const form = input.form;
      if (!form) return;
      try {
        const data = await uploadUserSettingsSubmittalPdf(field.id, form);
        // `viewPDF` (`:394-415`). ★ The collection is NOT updated — legacy's
        // raw `fetch` never touches the Backbone model — so the new URL lives
        // in view state only and a later full `render()` loses it.
        setPdfViews((prev) => ({ ...prev, [field.id]: { src: data.pdf_file ?? '', error: null } }));
      } catch (err) {
        showPdfError(field.id, err instanceof Error ? err.message : 'Failed to upload PDF.');
      }
    },
    [showPdfError]
  );

  /** `openUserDefinedFieldPopUp` (`:285-293`). */
  const onAddClick = useCallback(() => {
    typeFormatRef.current = 2; // ★ the NUMBER — see the mutation's docblock
    setTypePopupOpen(true);
  }, []);

  /** `addUserDefinedField` (`:257-284`) — §6 W1. */
  const addUserDefinedField = useCallback(async () => {
    // `remove_confirm_popup(undefined, '.add-file')` runs BEFORE the save.
    setTypePopupOpen(false);
    const top = railWrapperRef.current?.scrollTop ?? 0;
    let created: IUserSettingsSubmittalUdf;
    try {
      created = await createUdf({ udf_type_id: typeFormatRef.current }).unwrap();
    } catch {
      // Backbone's `.then` success branch simply never runs. No error UI exists.
      nameChangeRef.current = false;
      return;
    }
    // ★ Q11 — `collection.unshift(field)`: the new row renders FIRST, even
    // though the API sorts by `name`, so a refresh moves it alphabetically.
    const nextFields = [created, ...fieldsRef.current];
    doRender(nextFields, formatRef.current);
    patchUdfCache((draft) => {
      draft.unshift(created);
    });
    if (railWrapperRef.current) railWrapperRef.current.scrollTop = top;
    nameChangeRef.current = false;
    window.setTimeout(() => {
      // `:270-271` — the new library row is looked up by FIELD_ORDER, clicked,
      // and only THEN `loadPdf`-ed by hand (a click alone would leave the pane
      // empty — ★ Q1 again).
      const target = fieldsRef.current.find((f) => f.field_order === created.field_order);
      // `flushSync` so the pane really is in the DOM before the focus step
      // below queries for its input — legacy is fully synchronous here.
      flushSync(() => {
        if (target) {
          selectLibraryItem('user', {
            key: libraryItemKey('user', target.id),
            liId: String(target.id),
            anchorId: target.id,
            paneId: `submittal_user_defined_field_${target.id}`,
          });
          loadPdfForField(target.id);
        }
      });
      // `:272-278` — focus the title with the caret at the END, via the
      // clear-then-restore trick (setting `.value` moves the caret to the end).
      const input = document.querySelector<HTMLInputElement>(
        `#submmital-fields-data-wrapper input.title_user_defined_field_${created.id}`
      );
      if (input) {
        const val = input.value;
        input.value = '';
        input.value = val;
        input.focus();
      }
    }, 10);
  }, [createUdf, doRender, loadPdfForField, patchUdfCache, selectLibraryItem]);

  /** `render_popup` (`:620-636`) — the delete confirmation. Bound to
   * `button.remove`, so unlike the rail's `.delete` only ONE popup opens.
   * ⚠ Legacy also `console.log('testttttttttt', e)`s here (`:622`); the debug
   * line is not reproduced. */
  const onRemoveClick = useCallback((fieldId: number) => {
    selectedFieldRef.current = fieldId;
    const field = fieldsRef.current.find((f) => f.id === fieldId);
    if (field) setDeletePopupField(field);
  }, []);

  /** `removeUserDefinedField` (`:491-516`) — §6 W6. */
  const removeUserDefinedField = useCallback(() => {
    const id = selectedFieldRef.current;
    if (id == null) return;
    /**
     * `:494-495` — the sibling to activate afterwards is looked up as
     * `#submittal-row-container li.active.defined-li-<id>`, i.e. it exists ONLY
     * when the field being deleted is the currently-selected one. Otherwise
     * `sibling` is `undefined` and nothing is clicked.
     */
    const key = libraryItemKey('user', id);
    // `li.active.defined-li-<id>` — the selector matches whichever way the row
    // got its `.active`, so an additive highlight counts here too.
    const isActive = activeItemKeyRef.current === key || extraActiveKeysRef.current.has(key);
    const index = fieldsRef.current.findIndex((f) => f.id === id);
    const sibling = isActive
      ? fieldsRef.current[index + 1] ?? fieldsRef.current[index - 1]
      : undefined;

    setDeletePopupField(null);
    // Optimistic, exactly like Backbone's `destroy()` with no `{wait:true}`:
    // the row leaves the collection before the response and a failure is never
    // reverted. ★★ On the server this also triggers R4 — the collateral delete
    // of every format row whose `object_id` matches, across content types AND
    // across users. Not guarded here; the client cannot fix it.
    deleteUdf(id);
    patchUdfCache((draft) => {
      const i = draft.findIndex((f) => f.id === id);
      if (i >= 0) draft.splice(i, 1);
    });

    // `$('li.defined-li-<id>').remove()` + `$('#submittal_user_defined_field_<id>').remove()`
    // — the row and its pane leave the RENDERED markup immediately, without a
    // full re-render. (`$('.submittal_format_field option[value=…]')` targets
    // `<option>`s that no longer exist in any template — a no-op, not ported.)
    const nextFields = fieldsRef.current.filter((f) => f.id !== id);
    fieldsRef.current = nextFields;
    setSnap((prev) => (prev ? { ...prev, userFields: nextFields } : prev));

    /**
     * `:504-513` — re-index the format collection, dropping every row whose
     * `object_id` matches the deleted field.
     * ★ The filter is `el.object_id != self.selected_field` and NOTHING ELSE:
     * no content-type check. A format row pointing at a
     * `MasterSubmittalPredefinedField` that happens to share the id is dropped
     * too. That is the client-side twin of backend risk R4, and it is mirrored
     * rather than corrected.
     */
    const nextFormat: IUserSettingsSubmittalFormatRow[] = [];
    let order = 0;
    formatRef.current.forEach((row) => {
      if (row.object_id !== id) {
        nextFormat.push({ ...row, order_id: order });
        order += 1;
      }
    });
    formatRef.current = nextFormat;

    // `sibling?.click()` clicks the `<li>`, which Bootstrap's tab plugin does
    // NOT see (it is bound to the `<a>`), so only `clickToItem` runs — no pane
    // switch. `render_selects` re-clicks the ANCHOR 300 ms later, and that is
    // what finally activates it.
    if (sibling) {
      clickToItem('user', {
        key: libraryItemKey('user', sibling.id),
        liId: String(sibling.id),
        anchorId: sibling.id,
        paneId: `submittal_user_defined_field_${sibling.id}`,
      });
    }
    renderSelects();
  }, [clickToItem, deleteUdf, patchUdfCache, renderSelects]);

  /** `checkField` (`:417-426`) as bound to the CMS panes' READONLY title
   * inputs. Real (it toggles `.invalid-field`) but inert in practice — the
   * labels are always longer than three characters and the input cannot be
   * typed into. Q21's other half, the `done_btn` toggle, is dead. */
  const onTitleCheck = useCallback((docValue: string, input: HTMLInputElement) => {
    const invalid = input.value.length < 3;
    setInvalidTitles((prev) => {
      if (invalid === prev.has(docValue)) return prev;
      const next = new Set(prev);
      if (invalid) next.add(docValue);
      else next.delete(docValue);
      return next;
    });
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // WRITE-SIDE HANDLERS (U-d) — the format rail
  //
  // Legacy funnels all three through ONE jQuery-UI `stop` callback
  // (`makeSortable`, `:832-881`) that branches on `self.dropping`, plus the
  // two delegated `.delete` handlers. The split below is that same shape:
  //   `updateOrdering`  → the PATCH both drag paths share
  //   `changeOrder`     → W8's payload builder, with its skipped tail entry
  //   `addStructureField` / `createNewField` → W8
  //   `onReorderCommit` / `onRowPlainClick`  → W7 (and ★ Q22)
  //   `render_delete_popup_submittal` / `removeSubmittalFormatField` → W9
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * `updateOrdering` (`:744-763`) — §6 W7's single request, shared by both drag
   * paths.
   *
   * ★ NO RE-FETCH AND NO RE-RENDER. The response echoes the request (the
   * serializer returns `validated_data`, not DB state), and legacy uses it only
   * to REORDER the collection in memory:
   *
   *   data.settings.forEach(item => {
   *     collection.get(item.id).order_id = item.order_id;   // ← see below
   *     new_list.push(collection.get(item.id));
   *   });
   *   collection.set(new_list);
   *
   * Two things about those three lines are reproduced deliberately:
   *  · `model.order_id = …` sets a stray own-property on the Backbone model,
   *    NOT `model.attributes.order_id` — so `toJSON()` keeps reporting the OLD
   *    `order_id` values, in the NEW array order. Nothing on this page reads
   *    `order_id` (the rail's `data-index` is the array position and
   *    `changeOrder` counts array positions), which is the only reason it does
   *    not matter. The rows below are therefore reordered WITHOUT rewriting
   *    `order_id`, matching that exactly.
   *  · `collection.set(new_list)` runs with Backbone's default `{remove:true}`,
   *    so anything absent from `data.settings` LEAVES the collection. Mirrored.
   *    (Backbone also dedupes by id, which matters because `changeOrder` can
   *    legitimately emit the same id twice — see its note.)
   *
   * `updateOrdering`'s `fetch` parameter is never passed by either call site, so
   * its `fetch_submittal_format()` branch is dead; the `else` branch renumbers
   * `data-index`, which React does by array position. `this.scrollTop` is
   * likewise captured only for that dead branch and is not kept here.
   */
  const updateOrdering = useCallback(
    async (settings: IUserSettingsSubmittalOrderEntry[]) => {
      const data = await dragFormat({ settings }).unwrap();
      if (!data?.settings) return;
      const byId = new Map(formatRef.current.map((row) => [row.id, row]));
      const next: IUserSettingsSubmittalFormatRow[] = [];
      const seen = new Set<number>();
      data.settings.forEach((item) => {
        const row = byId.get(item.id);
        // ⚠ Legacy would THROW here on a miss (`collection.get(id).order_id =`
        // on `undefined`). Unreachable in practice — the server echoes back the
        // ids we just sent — and a thrown TypeError would only abandon the
        // reorder half-done, so the miss is skipped rather than staged.
        if (!row || seen.has(row.id)) return;
        seen.add(row.id);
        next.push(row);
      });
      formatRef.current = next;
    },
    [dragFormat]
  );

  /**
   * ★★ `changeOrder` (`:764-780`) — TRANSCRIBED LINE FOR LINE, INCLUDING THE
   * SKIPPED TAIL ENTRY. This is W8's second request and is NOT the same builder
   * as W7's.
   *
   * By the time it runs, `addStructureField` has already appended the newly
   * created row to the collection, so `models` is N+1 long and `last_index`
   * points AT THE NEW ROW. The loop then walks the collection emitting one
   * entry per model — except that it splices the new id in at the drop `index`
   * and SKIPS `last_index`, i.e. the new row's own entry at the tail. That is
   * the off-by-one; it is what makes the payload's shape depend on WHERE the
   * drop landed, and it is not to be tidied up:
   *
   *  · mid-list drop (`index < last_index`) — every model appears exactly once,
   *    the new one at `index` and everything from `index` on shifted by +1.
   *  · drop at the very END (`index === last_index`, which is what
   *    `prev.data-index + 1` yields when the item is released below the last
   *    row) — the `idx === index` branch fires for the new row ITSELF, so it
   *    emits `{id: new, order_id: N}` AND `{id: new, order_id: N+1}`: the same
   *    id twice, with two different orders, and the surviving `order_id` skips
   *    a number. Discovery §15.3 flagged the two extremes as unverified; this
   *    is what the code does at them, and it is what ships.
   *
   * Entries are `{ id, order_id }` — id FIRST, and a NUMBER, because they come
   * off `collection.toJSON()`. W7's come off the DOM and are the other way
   * round. See `IUserSettingsSubmittalOrderEntry`.
   */
  const changeOrder = useCallback(
    async (index: number, id: number) => {
      const models = formatRef.current;
      const lastIndex = models.length - 1;
      const arr: IUserSettingsSubmittalOrderEntry[] = [];
      models.forEach((element, idx) => {
        if (idx < index) arr.push({ id: element.id, order_id: idx });
        else if (idx === index)
          arr.push({ id, order_id: idx }, { id: element.id, order_id: idx + 1 });
        else if (idx !== lastIndex) arr.push({ id: element.id, order_id: idx + 1 });
      });
      await updateOrdering(arr);
    },
    [updateOrdering]
  );

  /** `addStructureField` (`:665-671`) — §6 W8's first request. `collection.add`
   * APPENDS (the collection declares no comparator), which is the premise
   * `changeOrder`'s `last_index` rests on. */
  const addStructureField = useCallback(async () => {
    const field = await createFormat().unwrap();
    formatRef.current = [...formatRef.current, field];
    return field;
  }, [createFormat]);

  /**
   * `fetch_submittal_format` (`:692-702`), reached through
   * `Dispatcher 'submittal:format:changed'`.
   *
   * ★ THE RE-FETCH BRANCH IS UNREACHABLE, so it is not ported. The event has
   * exactly one publisher — `ModelSubmittalFormat.onchange`
   * (`submittal/model/submittal.format.js:33-40`), which fires it after the
   * `save()` that a `set()` on a format model triggers. There are two such
   * `set()`s in the whole view: `createNewField` (`:798`), which assigns
   * `last_action = 'create_new_field'` on the line before, and
   * `updateSubmittalFormat` (`:673-690`), which is dead code (§10 — the
   * `<select>` markup it targets no longer exists in any template). So the guard
   * is ALWAYS true and the observable effect of the dispatcher is a full
   * client-side `render()` from the collection — never a GET.
   */
  const fetchSubmittalFormat = useCallback(() => {
    lastActionRef.current = '';
    doRender(fieldsRef.current, formatRef.current);
  }, [doRender]);

  /**
   * `createNewField` (`:786-831`) — §6 W8, the three-request dance, in order:
   *   1. `POST …/format/ {}`                    (`addStructureField`)
   *   2. `PATCH …/format/drag/ {settings:[…]}`  (`changeOrder`)
   *   3. `PATCH …/format/<id>` with the type payload
   *
   * ★ THE THIRD REQUEST IS NOT AWAITED BEFORE THE DOM CHANGES. Legacy's
   * `field.set(...)` fires `onchange` → `save()` synchronously and then carries
   * straight on to inject the row by hand; only the `.then` of that save reaches
   * the dispatcher and the full re-render. So the rail shows a hand-built row
   * for one round-trip, and if the PATCH fails it shows it forever — which is
   * the legacy behaviour, and the reason the injected row is modelled rather
   * than skipped.
   *
   * ★ `$nextRow` / `$prevRow`: legacy captures the drop's neighbours BEFORE the
   * awaits, with a comment saying exactly that, because `revert: true` yanks
   * `ui.item` out of the DOM while they are pending. `new_index` is already that
   * capture in numeric form — it was computed from the neighbours' `data-index`
   * in `stop` (`:846-847`) before any request went out — so the React port has
   * nothing left to race against.
   *
   * ★ Duplicates are allowed: dropping the same library item twice legitimately
   * produces two rows with the same `object_id`. Nothing dedupes, here or on the
   * server.
   */
  const createNewField = useCallback(
    async (payload: SubmittalDragPayload, newIndex: number) => {
      const targetType = payload.type;
      const targetName = payload.name;
      const targetId = payload.id;
      const text = payload.text;

      let field: IUserSettingsSubmittalFormatRow;
      try {
        field = await addStructureField();
        await changeOrder(newIndex, field.id);
      } catch {
        // Either request failing leaves legacy's `await` rejected and the rest
        // of `createNewField` unrun: no third PATCH, no injected row. (A failed
        // step 1 also means no orphan row; a failed step 2 leaves the blank
        // placeholder row that step 1 created, which is exactly what legacy
        // leaves behind.)
        return;
      }
      lastActionRef.current = 'create_new_field';

      // `field.set({object_id, type, cms_field_name})` → `onchange` →
      // `save(self.changed, {patch:true})`. Built in the legacy key order so the
      // JSON matches; the undefined members drop out (see the type's docblock).
      const patched = patchFormat({
        id: field.id,
        body: { object_id: targetId, type: targetType, cms_field_name: targetName },
      }).unwrap();
      /**
       * ⚠ The local `set()` that precedes the save is deliberately NOT mirrored.
       * It writes `object_id` / `type` / `cms_field_name` onto the collection
       * model, but nothing renders from the collection between here and the
       * full `render()` below — the injected row carries its own copy of those
       * values — and the save's response overwrites all three (plus `name`,
       * `model` and `content_type_id`) before that render happens. On the
       * failure path there is no render at all. So the write is unobservable in
       * both branches, and skipping it keeps `object_id` correctly typed.
       */

      // ── the synchronous half: the hand-injected row (`:808-830`) ──────────
      const injected: IUserSettingsSubmittalFormatRow = {
        // Synthetic — never rendered. The row's DOM id and its trash button's
        // `data-id` come from `domIdOverrides` below, because
        // `templateSelectItem` is fed the LIBRARY item's id.
        id: INJECTED_FORMAT_ROW_ID,
        user_id: field.user_id,
        object_id: targetId === undefined ? null : Number(targetId),
        content_type_id: null,
        order_id: newIndex,
        cms_field_name: targetName ?? null,
        model: SUBMITTAL_DRAG_MODELS[targetType],
        // `{{name}}` ← `data-text`, i.e. the LIBRARY label, not the server's
        // resolved `name`. They differ for CMS ("Shipping Terms & Conditions"
        // either way here, but the server is the authority) until the render.
        name: text,
        report_type: null,
      };
      setRail((prev) => {
        const rows = [...prev.rows];
        rows.splice(newIndex, 0, injected);
        return { ...prev, rows };
      });
      // `$wrapper.find(".empty-row").appendTo($wrapper)` — re-append the
      // sentinel so it stays last. `FormatRail` renders it after the rows by
      // construction, and renders none at all in the `renderSelects` variant,
      // which is the same no-op legacy's `find()` performs there.
      // `$wrapper.find(".select-row[data-index]").each(…)` — the renumbering is
      // likewise the map index.
      setDomIdOverrides({ [INJECTED_FORMAT_ROW_ID]: targetId ?? '' });

      // ── the async tail: the save's `.then` → the dispatcher → render() ────
      let updated: IUserSettingsSubmittalFormatRow;
      try {
        updated = await patched;
      } catch {
        // `.then` never runs → no dispatcher → no re-render, and the injected
        // row stays on screen. ★ This is the branch R7 would land in for a
        // `user_defined` drop with no `object_id`, and R8 for a missing `type`.
        return;
      }
      formatRef.current = formatRef.current.map((row) =>
        row.id === field.id ? { ...row, ...updated } : row
      );
      fetchSubmittalFormat();
    },
    [addStructureField, changeOrder, fetchSubmittalFormat, patchFormat]
  );

  /**
   * The DOM-order payload builder shared by W7's two triggers — `stop`'s
   * non-dropping branch (`:851-860`) verbatim:
   *
   *   Array.from($$('#submittal-format-list-wrapper-row .select-row[data-index]'))
   *     .map((row, order_id) => ({ order_id, id: row.id.replace('row-','') }))
   *
   * ★ It reads the RENDERED rail, not the collection — hence `rail.rows` — and
   * `order_id` comes FIRST with `id` a STRING sliced off the DOM id. The
   * `[data-index]` filter is what excludes the `.empty-row` sentinel; here that
   * is automatic, since the sentinel is not one of `rail.rows`. A hand-injected
   * row IS included, and contributes its borrowed DOM id, exactly as legacy's
   * `row.id.replace('row-','')` would.
   */
  const domOrderPayload = useCallback(
    (): IUserSettingsSubmittalOrderEntry[] =>
      rail.rows.map((row, order_id) => ({
        order_id,
        id: domIdOverrides[row.id] ?? String(row.id),
      })),
    [domIdOverrides, rail.rows]
  );

  /** Live shuffle during an internal drag — the sortable moving its items. Only
   * the rendered rail moves; the collection follows from the RESPONSE, in
   * `updateOrdering`. */
  const onRowsMove = useCallback((from: number, to: number) => {
    setRail((prev) => {
      if (from === to || from < 0 || to < 0 || from >= prev.rows.length || to >= prev.rows.length) {
        return prev;
      }
      const rows = [...prev.rows];
      const [moved] = rows.splice(from, 1);
      rows.splice(to, 0, moved);
      return { ...prev, rows };
    });
  }, []);

  /** §6 W7 — the reorder PATCH. ★ Fired on EVERY drag end, movement or not:
   * jQuery-UI's `stop` has no "did anything change" test and neither does this. */
  const onReorderCommit = useCallback(() => {
    void updateOrdering(domOrderPayload()).catch(() => {
      // `fn.uni_tools.patch` throws and `stop` does not catch — an uncaught
      // rejection, with the rail left showing the dropped order. Same outcome
      // here, minus the console noise.
    });
  }, [domOrderPayload, updateOrdering]);

  /**
   * ★★ Q22 — A PLAIN CLICK ON A RAIL ROW EMITS A FULL `/drag/` PATCH.
   *
   * jQuery-UI fires the sortable's `stop` for a zero-distance drag, so simply
   * clicking a row puts W7's request on the wire carrying the ordering it
   * already had. It is a legacy bug and it is on the wire, so it is reproduced
   * rather than fixed. Identical payload to `onReorderCommit` — `FormatRail`
   * decides WHEN (never for a press on the trash button, which jQuery-UI's
   * `cancel` option excludes from sorting, and never straight after a real
   * drag, where jQuery-UI sets `preventClickEvent`).
   */
  const onRowPlainClick = onReorderCommit;

  /** The library-drag half of `stop` (`:845-850`) → `createNewField`. */
  const onCreateAtIndex = useCallback(
    (index: number) => {
      const payload = dragPayloadRef.current;
      if (!payload) return;
      void createNewField(payload, index);
    },
    [createNewField]
  );

  /**
   * ★ U-d — `init_draggable($(".submittal-draggable-item"))` (`:862-878`): the
   * library half of the drag, armed by every anchor's `mousedown` and dropping
   * only on `#submittal-format-list-wrapper-row`.
   */
  const libraryDrag = useLibraryDrag<SubmittalDragPayload>({
    railRef,
    scrollerRef: railWrapperRef,
    dragPayloadRef,
    onDrop: onCreateAtIndex,
  });

  /**
   * `render_delete_popup_submittal` (`:638-646`) — §6 W9's popup.
   * ★ Q4: called TWICE per click by `FormatRail`, appending two identical
   * popups. `self.selected_field_id` is overwritten by the second call with the
   * same value, so the DELETE is unaffected.
   */
  const onFormatDeleteClick = useCallback((domId: string) => {
    selectedFieldIdRef.current = domId;
    const row = formatRef.current.find((r) => String(r.id) === domId);
    // ⚠ Legacy does `collection.get(id).toJSON()` unguarded, so a miss throws an
    // uncaught TypeError and no popup appears. The only way to miss is the
    // hand-injected row, whose `data-id` is the library item's (and `''` for a
    // CMS drop). Same observable outcome — no popup — without the console error,
    // which is how Q2 was handled on the page shell.
    if (!row) return;
    setDeleteFormatPopups((prev) => [...prev, row]);
  }, []);

  /** `removeSubmittalFormatField` (`:704-714`) — §6 W9. */
  const removeSubmittalFormatField = useCallback(() => {
    const domId = selectedFieldIdRef.current;
    if (domId == null) return;
    // `$('#row-<id>').remove()` — the row leaves the RENDERED rail BEFORE the
    // request goes out, and `data-index` renumbers itself from the map position.
    setRail((prev) => ({ ...prev, rows: prev.rows.filter((r) => String(r.id) !== domId) }));
    // `remove_confirm_popup(undefined, '.delete-field')` removes the jQuery SET
    // — i.e. BOTH of Q4's popups.
    setDeleteFormatPopups([]);
    const row = formatRef.current.find((r) => String(r.id) === domId);
    if (!row) return;
    // Backbone `destroy()`: optimistic, never reverted, and the model leaves the
    // collection immediately. ⚠ R3 — neither side re-indexes `order_id`, so the
    // table keeps a hole until the next full ordering PATCH. Not compensated.
    deleteFormat(row.id);
    formatRef.current = formatRef.current.filter((r) => r.id !== row.id);
  }, [deleteFormat]);

  // `#submittal_format` stays EMPTY until all five fetches resolve — the view's
  // first `render()` lives inside `Promise.all(...).then(...)`.
  if (!snap) return null;

  /** The exclusive `.active` (Bootstrap's tab plugin) UNION the additive one
   * (`active_last_change`) — see `extraActiveKeys`. Legacy can have both at
   * once, so the library renders from a set, not a scalar. */
  const activeItemKeys: ReadonlySet<string> = extraActiveKeys.size
    ? new Set(activeItemKey ? [activeItemKey, ...extraActiveKeys] : [...extraActiveKeys])
    : new Set(activeItemKey ? [activeItemKey] : []);

  return (
    <>
      {/* ★ Q3 — `submittal.tmpl:1`'s root id collides with the rail anchor in
          `main.tmpl:22` (and, once U-e lands, with the proposal pane's root,
          which is ALSO `id="submittal"`). Three elements share it on the real
          page; `document.getElementById('submittal')` resolves to the nav
          anchor. Load-bearing — `styles.css` selects `#submittal .…` and CSS id
          selectors match every one of them. Kept. */}
      <div id="submittal" className="col-xs-12 full-height">
        <div className="full-height">
          <div className="col-xs-12 full-height">
            <div className="row full-height">
              <FormatRail
                rows={rail.rows}
                variant={rail.variant}
                activeRowId={activeRowId}
                labelOverrides={railLabelOverrides}
                domIdOverrides={domIdOverrides}
                tooltipRowIds={tooltipRowIds}
                onRowClick={onRowClick}
                onRowMouseOver={onRowMouseOver}
                onWrapperScroll={onWrapperScroll}
                wrapperRef={railWrapperRef}
                labelRefs={labelRefs}
                railRef={railRef}
                libraryPlaceholderIndex={libraryDrag.placeholderIndex}
                onRowsMove={onRowsMove}
                onReorderCommit={onReorderCommit}
                onRowPlainClick={onRowPlainClick}
                onDeleteClick={onFormatDeleteClick}
              />
              <div className="col-xs-9 full-height">
                <div className="row full-height right-column user_settings-content-user-fields">
                  <FieldLibrary
                    userFields={snap.userFields}
                    cmsDocuments={cmsDocuments}
                    appFields={appFields}
                    activeType={activeType}
                    counts={counts}
                    searchValue={searchValue}
                    searchHidden={searchHidden}
                    onSearchChange={setSearchValue}
                    onChipClick={changeActiveDefinedsField}
                    onAddClick={onAddClick}
                    onItemClick={selectLibraryItem}
                    onUserAnchorMouseOver={(field) => loadPdfForField(field.id)}
                    onRemoveClick={onRemoveClick}
                    activeItemKeys={activeItemKeys}
                    labelOverrides={labelOverrides}
                    tooltipItemKeys={tooltipItemKeys}
                    itemRefs={itemRefs}
                    anchorRefs={anchorRefs}
                    onItemMouseDown={libraryDrag.onAnchorMouseDown}
                  />
                  <hr />
                  <FieldDetail
                    userFields={snap.userFields}
                    appFields={appFields}
                    cmsDocuments={cmsDocuments}
                    terms={terms}
                    activePaneId={activePaneId}
                    panesWithIn={panesWithIn}
                    filledIds={filledIds}
                    onTitleCheck={onTitleCheck}
                    invalidTitles={invalidTitles}
                    renderUserPane={(field) => {
                      const view = pdfViews[field.id] ?? {
                        src: field.pdf_file ?? '',
                        error: null,
                      };
                      return (
                        <UserFieldPane
                          field={field}
                          pdfSrc={view.src}
                          pdfError={view.error}
                          onTitleBlur={onTitleBlur}
                          onContentBlur={onContentBlur}
                          onNewPageChange={onNewPageChange}
                          onPdfFileChange={onPdfFileChange}
                        />
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* All three popups are `append`ed into `#submittal_format` by legacy —
            the pane, not `<body>`. Rendered inside the same root here. */}
        {typePopupOpen && (
          <UdfTypePopup
            options={typeOptions ?? []}
            onTypeChange={(dataId) => {
              typeFormatRef.current = dataId;
            }}
            onAdd={addUserDefinedField}
            onCancel={() => setTypePopupOpen(false)}
          />
        )}
        {deletePopupField && (
          <DeleteUdfPopup
            field={deletePopupField}
            onConfirm={removeUserDefinedField}
            onCancel={() => setDeletePopupField(null)}
          />
        )}
        {/* ★★ Q4 — TWO of these per click, stacked and identical. Cancel clears
            both, because `remove_confirm_popup` removes the jQuery SET. The key
            is the array position for exactly that reason: the two entries are
            the same row and must both render. */}
        {deleteFormatPopups.map((format, i) => (
          <DeleteFormatFieldPopup
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            format={format}
            onConfirm={removeSubmittalFormatField}
            onCancel={() => setDeleteFormatPopups([])}
          />
        ))}
      </div>
    </>
  );
};

export default SubmittalTab;
